import type { Placement, ReferenceType } from '@floating-ui/react';
import {
  arrow,
  autoUpdate,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import Surface from 'components/Surface';
import type { RefAttributes } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import styles from './Popover.module.scss';
import {
  ARROW_SVG_HEIGHT,
  ARROW_SVG_WIDTH,
  mergeRefs,
  useArrowStyle,
  useDismissOnOutsidePointerUp,
} from './utils';

interface PopoverProps {
  /** Contents of the popover. */
  children: React.ReactElement | ((position: Placement) => React.ReactElement);

  /** Class name for the content container of the popover. */
  className?: string;

  /**
   * If true, the popover will be closed when the user presses the Escape key.
   * @default true
   */
  closeOnEsc?: boolean;

  /**
   * If true, the popover will be closed when the user clicks outside of the popover.
   * @default true
   */
  closeOnOutsideClick?: boolean;

  /**
   * If true, will use fixed positioning instead of absolute positioning.
   * @default false
   */
  fixed?: boolean;

  /** Override the popover's internal state to force it open. */
  isOpen?: boolean;

  /** Offset of the popover relative to its target. */
  offset?: [mainAxis: number, crossAxis: number];

  /** Called when the popover closes. */
  onClose?: () => void;

  /** Called when the popover opens. */
  onOpen?: () => void;

  /** Position of the popover relative to its target. */
  position?: Placement;

  /** Visible element used to position and open/close this popover. */
  target: React.ReactElement;

  /**
   * If true, will display an arrow pointing to the target element.
   * @default true
   */
  targetArrow?: boolean;

  /**
   * If true, the contents will be wrapped in a <Surface> component.
   * @default true
   */
  withSurface?: boolean;
}

const Popover2: React.FC<PopoverProps> = ({
  children,
  className,
  closeOnEsc = true,
  closeOnOutsideClick = true,
  fixed = false,
  isOpen,
  offset: offsetValue,
  onOpen,
  onClose,
  position,
  target,
  targetArrow = true,
  withSurface = true,
}) => {
  const [isOpenLocalState, setIsOpenLocalState] = useState(!!isOpen);
  const arrowRef = useRef<HTMLDivElement>(null);

  const isDefinitelyOpen = isOpen ?? isOpenLocalState;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
      setIsOpenLocalState(open);
    },
    [onClose, onOpen]
  );

  const middleware = [flip(), shift({ crossAxis: true })];
  if (targetArrow) {
    middleware.push(arrow({ element: arrowRef }));
  }
  if (offsetValue) {
    middleware.push(offset({ mainAxis: offsetValue[0] ?? 0, crossAxis: offsetValue[1] ?? 0 }));
  }

  const nodeId = useFloatingNodeId();
  const {
    x,
    y,
    refs,
    strategy,
    context,
    placement: actualPlacement,
  } = useFloating({
    open: isDefinitelyOpen,
    onOpenChange: handleOpenChange,
    middleware,
    nodeId,
    placement: position,
    strategy: fixed ? 'fixed' : 'absolute',
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useRole(context),
    useDismiss(context, {
      escapeKey: closeOnEsc,
      outsidePress: closeOnOutsideClick,
    }),
    useDismissOnOutsidePointerUp(context, closeOnOutsideClick),
  ]);

  const arrowStyle = useArrowStyle(context);

  const childrenOrWrapped = React.isValidElement(children)
    ? children
    : (children as (position: Placement) => React.ReactElement)(actualPlacement);

  return (
    <FloatingNode id={nodeId}>
      {React.cloneElement(
        target,
        getReferenceProps({
          ref: mergeRefs(refs.setReference, (target as RefAttributes<ReferenceType>).ref),
          ...target.props,
        })
      )}
      <FloatingPortal id="floating-ui-root">
        {isDefinitelyOpen && (
          <FloatingFocusManager
            context={context}
            order={['content', 'reference']}
            returnFocus={false}
          >
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                style: {
                  position: strategy,
                  top: y,
                  left: x,
                },
                className,
              })}
            >
              {withSurface ? (
                <Surface className={styles.popover}>{childrenOrWrapped}</Surface>
              ) : (
                childrenOrWrapped
              )}
              {targetArrow && (
                <div ref={arrowRef} style={arrowStyle} className={styles.arrow}>
                  <svg
                    width={ARROW_SVG_WIDTH}
                    height={ARROW_SVG_HEIGHT}
                    viewBox={`0 0 ${ARROW_SVG_WIDTH} ${ARROW_SVG_HEIGHT}`}
                  >
                    <polygon
                      points={`0,${ARROW_SVG_HEIGHT} ${ARROW_SVG_WIDTH},${ARROW_SVG_HEIGHT} ${
                        ARROW_SVG_WIDTH / 2
                      },0`}
                    />
                    <polyline
                      points={`${ARROW_SVG_WIDTH},${ARROW_SVG_HEIGHT} ${
                        ARROW_SVG_WIDTH / 2
                      },0 0,${ARROW_SVG_HEIGHT}`}
                    />
                  </svg>
                </div>
              )}
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </FloatingNode>
  );
};

export default Popover2;
